import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, GraduationCap } from "lucide-react";

const EMPTY = { name: "", entity: "", date: "", expiry: "" };

export default function BulkCourseDialog({ open, employees, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [names, setNames] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setSelected(employees.filter((e) => e.active !== false).map((e) => e.id));
    api.get("/courses/names").then((r) => setNames(r.data)).catch(() => {});
  }, [open, employees]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const onDate = (v) => {
    set("date", v);
    if (v && !form.expiry) {
      const d = new Date(v); d.setFullYear(d.getFullYear() + 3);
      set("expiry", d.toISOString().slice(0, 10));
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Indica el nombre del curso");
    if (selected.length === 0) return toast.error("Selecciona al menos un empleado");
    setSaving(true);
    try {
      const r = await api.post("/courses/bulk", { ...form, employee_ids: selected });
      toast.success(`Curso aplicado: ${r.data.updated} actualizados, ${r.data.created} añadidos`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo aplicar el curso");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl w-[calc(100vw-1rem)] sm:w-full max-h-[95vh] flex flex-col p-4 sm:p-6" data-testid="bulk-course-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-accent" /> Fechas de curso en bloque
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">
          Si el empleado ya tiene el curso se actualizan sus fechas; si no lo tiene, se añade.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Curso *</Label>
            <Input list="course-names" value={form.name} onChange={(e) => set("name", e.target.value)}
              placeholder="Ej. AL — Trabajos en Altura" data-testid="bulk-course-name-input" />
            <datalist id="course-names">{names.map((n) => <option key={n} value={n} />)}</datalist>
          </div>
          <div className="space-y-1.5">
            <Label>Fecha realización</Label>
            <Input type="date" value={form.date} onChange={(e) => onDate(e.target.value)} data-testid="bulk-course-date-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Caducidad</Label>
            <Input type="date" value={form.expiry} onChange={(e) => set("expiry", e.target.value)} data-testid="bulk-course-expiry-input" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Entidad formadora</Label>
            <Input value={form.entity} onChange={(e) => set("entity", e.target.value)} />
          </div>
        </div>

        <div className="flex items-center justify-between mt-2">
          <Label>Empleados ({selected.length} seleccionados)</Label>
          <div className="flex gap-2 text-xs">
            <button className="text-accent hover:underline" onClick={() => setSelected(employees.map((e) => e.id))} data-testid="bulk-select-all">Todos</button>
            <button className="text-muted-foreground hover:underline" onClick={() => setSelected([])} data-testid="bulk-select-none">Ninguno</button>
          </div>
        </div>
        <div className="border rounded-lg overflow-y-auto max-h-56 divide-y" data-testid="bulk-employee-list">
          {employees.map((e) => (
            <label key={e.id} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" className="h-4 w-4 accent-primary" checked={selected.includes(e.id)}
                onChange={() => toggle(e.id)} data-testid={`bulk-emp-${e.id}`} />
              <span className="flex-1 truncate">{e.name}</span>
              {e.active === false && <span className="text-[10px] text-slate-500">Baja</span>}
              <span className="font-mono text-xs text-muted-foreground">{e.dni}</span>
            </label>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="bulk-course-save-button">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Aplicar a {selected.length} empleados
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
